import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Button, Typography } from '@mui/material';
import Image from 'next/image';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import { CtaLink } from '@repo/segment/components/CtaLink';
import { LinkType, MonetizableEnum } from '@repo/segment/enums';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph-schemas/marketplace/enums';
import type { MultiProductBannerOfferType } from '@repo/supergraph-schemas/marketplace/multiProductBanner/types';
import { CtaButton } from '../../../../CtaButton';
import { DisclosureTooltip } from '../../../../DisclosureTooltip';
import { getHighlightDetailsByOfferId } from './constants';

type Props = {
  highlightColor?: string;
  highlightTextColor?: string;
  impressionSection: string;
  impressionPosition: number;
  marketplaceEntity: MultiProductBannerOfferType;
};

export const ProductCard = ({
  highlightColor,
  highlightTextColor,
  impressionSection,
  impressionPosition,
  marketplaceEntity,
}: Props) => {
  const {
    cta,
    displayName,
    driverApy,
    driverBonus,
    driverMinimumBalance,
    id,
    institution,
    image,
    federallyInsuredAnnotation,
    monetizable,
    name,
    product,
    productType,
  } = marketplaceEntity;

  const highlightDetails = getHighlightDetailsByOfferId(id, {
    driverBonus,
    driverApy,
    driverMinimumBalance,
  });

  let ctaSubtext = cta?.subtext;
  if (cta && cta.subtext && federallyInsuredAnnotation) {
    ctaSubtext = `${cta.subtext}, ${federallyInsuredAnnotation}`;
  }

  return (
    <AnalyticsSkuViewed
      source={SEGMENT_SKU_VIEWED_SOURCE}
      name={displayName}
      productType={productType}
      level="offer"
      institutionId={institution?.id}
      institutionName={institution?.name}
      productId={product?.id}
      productName={product?.name}
      offerId={id}
      offerName={name}
      monetizable={monetizable}
      impressionSection={impressionSection}
      impressionPosition={impressionPosition}
      additionalAttributes={{ page_type: 'roundup' }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateRows: {
            xs: `auto 20px auto auto 16px auto 8px auto 8px auto 20px`,
            sm: `auto 20px 1fr auto 16px auto 8px auto 8px 1fr 20px`,
          },
          gridColumn: 'span 1',
          height: '100%',
          border: '1px solid',
          borderColor: 'neutral.lighter',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            backgroundColor: highlightDetails?.text ? highlightColor : 'white',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Box
            sx={{
              py: 2,
            }}
          >
            {highlightDetails?.text && (
              <Box sx={{ display: 'inline', alignItems: 'center', justifyContent: 'center' }}>
                <Typography
                  variant="textExtraSmall"
                  sx={{ color: highlightTextColor, fontSize: 13, fontWeight: 500 }}
                >
                  {highlightDetails.text}
                </Typography>
                <Box
                  component="span"
                  sx={{ display: 'inline', whiteSpace: 'nowrap', verticalAlign: 'center' }}
                >
                  &nbsp;
                  {highlightDetails?.tooltip && (
                    <DisclosureTooltip
                      placement="top"
                      tooltipStyleOverride={{
                        paddingX: 3,
                        maxWidth: '232px',
                        color: 'neutral.darker',
                      }}
                      trigger={
                        <Button
                          aria-label="Product highlight info"
                          disableTouchRipple
                          variant="tooltip"
                          sx={{
                            width: '16px',
                          }}
                        >
                          <HelpOutlineIcon
                            sx={{
                              width: 16,
                              height: 16,
                              color: highlightTextColor,
                            }}
                          />
                        </Button>
                      }
                    >
                      <Typography variant="textSmall" sx={{ fontSize: 14 }}>
                        {highlightDetails.tooltip}
                      </Typography>
                    </DisclosureTooltip>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            px: { xs: 3, lg: 3.5 },
          }}
        >
          <Typography variant="textSmall">{displayName}</Typography>
        </Box>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            position: 'relative',
          }}
        >
          {image?.source && (
            <Box
              sx={{
                px: { xs: 3, lg: 3.5 },
                pt: 2,
                position: 'relative',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
              }}
            >
              <CtaLink
                cta={{
                  link: cta.link,
                  type: EntityPlacementCtaTypeEnum.ProductImage,
                  text: cta.text,
                }}
                monetizable={monetizable ?? MonetizableEnum.no}
                analyticsPayload={{
                  index: impressionPosition,
                  offerId: id,
                  institutionId: institution?.id,
                  productId: product?.id,
                  productType,
                }}
                linkType={LinkType.PRODUCT_IMAGE}
                sx={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    maxWidth: '160px',
                    height: '60px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mx: 'auto',
                  }}
                >
                  <Image
                    src={image.source}
                    alt={image.alt ?? ''}
                    fill
                    style={{ objectFit: 'contain' }}
                  />
                </Box>
              </CtaLink>
            </Box>
          )}
        </Box>

        <Box />
        <Box
          sx={{
            mx: { xs: 3, lg: 3.5 },
            backgroundColor: 'blues.lightest',
            borderRadius: '8px 8px 0 0',
          }}
        >
          <Box>
            {driverApy?.value && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'row',
                  pt: 2,
                }}
              >
                <Box sx={{ display: 'inline', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography
                    variant="textSmallBold"
                    sx={{ color: 'neutral.darkest', fontSize: 14 }}
                  >
                    APY: {driverApy.value}
                  </Typography>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline',
                      whiteSpace: 'nowrap',
                      verticalAlign: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {highlightDetails?.tooltip && (
                      <DisclosureTooltip
                        tooltipStyleOverride={{
                          maxWidth: '275px',
                          paddingX: '1rem',
                          paddingY: '0.5rem',
                        }}
                        trigger={
                          <Button
                            aria-label="NerdWallet APY info"
                            disableTouchRipple
                            variant="tooltip"
                            sx={{
                              width: '20px',
                              height: '20px',
                            }}
                          >
                            <HelpOutlineIcon
                              sx={{
                                width: 16,
                                height: 16,
                                color: 'neutral.dark',
                                mb: '2px',
                              }}
                            />
                          </Button>
                        }
                      >
                        <Typography variant="textSmall" sx={{ fontSize: 14 }}>
                          {driverApy.valueTooltip}
                        </Typography>
                      </DisclosureTooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Box>
            {driverApy?.description && (
              <Box
                sx={{
                  textAlign: 'center',
                  px: 2,
                  lineHeight: 1.2,
                }}
              >
                {driverApy && (
                  <Typography
                    variant="textExtraSmall"
                    sx={{ color: 'neutral.dark', letterSpacing: '0.5px' }}
                  >
                    {driverApy.description}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ backgroundColor: 'blues.lightest', mx: { xs: 3, lg: 3.5 } }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mx: { xs: 3, lg: 3.5 },
            backgroundColor: 'blues.lightest',
            borderRadius: '0 0 8px 8px',
          }}
        >
          <CtaButton
            fullWidth
            cta={cta}
            analyticsPayload={{
              index: impressionPosition,
              offerId: id,
              institutionId: institution?.id,
              productId: product?.id,
              productType,
              monetizable: monetizable ?? MonetizableEnum.no,
            }}
            hideSubtext
            buttonStylesOverride={{ borderRadius: 1 }}
          />
        </Box>

        <Box />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'start',
            px: { xs: 3, lg: 3.5 },
          }}
        >
          <Typography
            variant="textExtraSmall"
            textAlign="center"
            sx={{ color: 'neutral.dark', letterSpacing: '0.5px' }}
          >
            {ctaSubtext}
          </Typography>
        </Box>

        <Box />
      </Box>
    </AnalyticsSkuViewed>
  );
};
